<header class="go-page-title">
  <h1 class="go-heading-1">{{ pageTitle }}</h1>
  <a
  [href]="linkToSource"
  target="_blank">
    <go-button buttonVariant="neutral"> View on GitHub </go-button>
  </a>
</header>

<div class="go-container">
  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <!-- Intro -->
        <p class="go-body-copy">
          The <code class="code-block--inline">&lt;go-icon-button&gt;</code>
          component should be used for buttons that have no text.
        </p>
      </div>

      <div class="go-column go-column--100">
        <!-- Bindings -->
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="componentBindings"></code>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">buttonDisabled</h4>
        <p class="go-body-copy">
          Disables the button. This should be handled by the parent
          component.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">buttonIcon</h4>
        <p class="go-body-copy">
          Changes the icon that is displayed within the button. See the
          <a class="go-link" href="https://material.io/tools/icons/">Material Design System Icons</a>
          page for available icons.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">buttonSize</h4>
        <p class="go-body-copy">
          Changes the <code class="code-block--inline">font-size</code> property of the icon.
          This attribute defaults to "small", and the size options are as follows:
        </p>
        <ol class="go-list go-list--ordered">
          <li><code class="code-block--inline">small</code></li>
          <li><code class="code-block--inline">medium</code></li>
          <li><code class="code-block--inline">large</code></li>
        </ol>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h4 class="go-heading-4">buttonTitle</h4>
        <p class="go-body-copy">
          Changes the <code class="code-block--inline">title</code> attribute of the button,
          causing a tooltip to be displayed when hovering over the button.
        </p>
      </div>
    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="button-disabled">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button Disabled</h2>
      <go-copy cardId="button-disabled" goCopyCardLink></go-copy>
    </ng-container>
    <section class="go-container" go-card-content>
      <div class="go-column go-column--50">
        <code [highlight]="disabledExample"></code>
      </div>
      <div class="go-column go-column--50">
        <go-icon-button
          (handleClick)="testClick()"
          buttonIcon="home"
          buttonDisabled="true">
        </go-icon-button>
      </div>
    </section>
  </go-card>

  <go-card class="go-column go-column--100" id="button-icon">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button Icon</h2>
      <go-copy cardId="button-icon" goCopyCardLink></go-copy>
    </ng-container>
    <section class="go-container" go-card-content>
      <div class="go-column go-column--50">
        <code [highlight]="iconExample"></code>
      </div>
      <div class="go-column go-column--50">
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="home">
          </go-icon-button>
        </div>
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
             buttonIcon="school">
            </go-icon-button>
        </div>
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="work">
          </go-icon-button>
        </div>
      </div>
    </section>
  </go-card>

  <go-card class="go-column go-column--100" id="button-size">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button Size</h2>
      <go-copy cardId="button-size" goCopyCardLink></go-copy>
    </ng-container>
    <section class="go-container" go-card-content>
      <div class="go-column go-column--50">
        <code [highlight]="sizeExample"></code>
      </div>
      <div class="go-column go-column--50">
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="home"
            buttonSize="small">
          </go-icon-button>
        </div>
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="home"
            buttonSize="medium">
          </go-icon-button>
        </div>
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="home"
            buttonSize="large">
          </go-icon-button>
        </div>
      </div>
    </section>
  </go-card>

  <go-card class="go-column go-column--100" id="button-title">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Button Title</h2>
      <go-copy cardId="button-title" goCopyCardLink></go-copy>
    </ng-container>
    <section class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">Hover the icons to display their tooltips.</p>
      </div>

      <div class="go-column go-column--50">
        <code [highlight]="titleExample"></code>
      </div>
      <div class="go-column go-column--50">
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="home"
            buttonTitle="Home">
          </go-icon-button>
        </div>
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="school"
            buttonTitle="School">
          </go-icon-button>
        </div>
        <div class="go-column">
          <go-icon-button
            (handleClick)="testClick()"
            buttonIcon="work"
            buttonTitle="Work">
          </go-icon-button>
        </div>
      </div>
    </section>
  </go-card>
</div>

